import React, { useCallback, useEffect, useState } from "react";
import "./shareTicketModal.scss";
import Portal from "elements/Portal";
import Sheet from "components/sheet/Sheet";
import { useTranslation } from "react-i18next";
import { ITicket } from "redux/reducers/models/ticket";
import { GetCustomerFriends, ICustomerFriend } from "api/rpc/2024-04/customer/customer";
import axios, { CancelToken } from "axios";
import { StatusCode } from "api/protocols";
import { useAppDispatch } from "hooks/redux";
import { showError } from "redux/actions/ui";
import { Select } from "components/select/index";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faUsersSlash } from "@fortawesome/pro-regular-svg-icons";
import ReactDOM from "react-dom";
import { ButtonNew as Button } from "components/buttonNew";
import Callout from "components/callout/Callout";
import Divider from "components/divider";
import FormLayout from "components/form/FormLayout";
import Input from "components/form/input";
import validator from "validator";
import { useWindowSize } from "hooks/useWindowSize/useWindowSize";
import { MOBILE_WIDTH } from "helpers/ScreenSizes";
import Panel from "components/panel/Panel";
import { GetCustomerTicketSharedUsers } from "api/rpc/2024-04/customer/ticket";
import Spin from "components/spin/spin";
import Popup from "components/popup/Popup";

interface IProps {
    open: boolean;
    ticket: ITicket;
    onCancel: () => void;
    onOk: (users: Array<{id: number | null, first_name: string; last_name: string; email: string}>) => void;
}

export default function ShareTicketModal(props: IProps) {
    const { open, ticket, onCancel, onOk } = props;
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const windowSize = useWindowSize();
    const { Option } = Select;
    const [friends, setFriends] = useState<Array<ICustomerFriend>>([]);
    const [friendSearch, setFriendSearch] = useState<string>("");
    const [selectedCustomers, setSelectedCustomers] = useState<Array<ICustomerFriend>>(null);
    const [ticketShared, setTicketShared] = useState<boolean>(false);
    const [loading, setLoading] = useState<boolean>(false);
    const [newRecipient, setNewRecipient] = useState<{open: boolean; firstName: string; lastName: string; email: string}>({
        open: false,
        firstName: "",
        lastName: "",
        email: "",
    });

    const canShareTicket = ticket?.share_limit > 0 ;

    useEffect(() => {
        const source = axios.CancelToken.source();
        if (open) {
            //Get users if ticket is shareable
            if (canShareTicket) {
                void getSharedUsers(source.token);
            }
            //Get friends list if not already loaded
            if (!friends || friends?.length === 0) {
                void getFriends(source.token);
            }
        } else {
            ReactDOM.unstable_batchedUpdates(() => {
                setFriendSearch("");
                setSelectedCustomers(null);
                setNewRecipient({open: false, firstName: "", lastName: "", email: ""});
                setTicketShared(false);
            });
        }
        return () => source.cancel();
    }, [open]);

    async function getFriends(cancelToken: CancelToken) {
        //Loader
        if (friends !== undefined) {
            setFriends(undefined);
        }
        const res = await GetCustomerFriends(null, false, cancelToken);
        if (res?.status !== StatusCode.OK) {
            setFriends([]);
            if (cancelToken && cancelToken?.reason) {
                return;
            }
            dispatch(showError(t("Error getting friends list")));
            return;
        }
        setFriends(res?.data);
    }

    async function getSharedUsers(cancelToken: CancelToken) {
        setLoading(true);
        const res = await GetCustomerTicketSharedUsers({ticket_id: ticket?.id}, false, cancelToken);
        setLoading(false);
        if (res?.status !== StatusCode.OK) {
            setSelectedCustomers([]);
            if (cancelToken && cancelToken?.reason) {
                return;
            }
            dispatch(showError(typeof res?.data === "string" ? res?.data : t("Error getting shared users")));
            return;
        }
        if (res?.data?.length > 0) {
            setTicketShared(true);
        }
        setSelectedCustomers(res?.data as Array<ICustomerFriend>);
    }

    function handleOnOk() {
        const users = selectedCustomers?.map(customer => ({id: customer?.id ?? null, first_name: customer?.first_name, last_name: customer?.last_name, email: customer?.email}));
        void onOk(users);
    }

    /** Determines whether to use Panel component for mobile screen sizes or Sheet for desktop screen sizes  */
    const ShareTicketWrapper = useCallback(({children}: {children: React.ReactNode}) => {
        return windowSize.width <= MOBILE_WIDTH ? 
        (<Panel
        open={open}
        title={t("Share ") + `${ticket?.title ?? t("Ticket")}`}
        subtitle={`${t("Share limit: ")} ${ticket?.share_limit}`}
        onClose={onCancel}
        pagePercent={80}
        animate
        primaryAction={{label: t("Confirm"), onClick: () => handleOnOk(), disabled: loading}}
        secondaryAction={{label: t("Cancel"), onClick: () => onCancel()}}
        >
            {children}
        </Panel> )
        : 
       ( <Portal isMounted={open}>
            <Sheet
            open={open}
            title={t("Share ") + `${ticket?.title ?? t("Ticket")}`}
            size="medium"
            onCancel={onCancel}
            onOk={handleOnOk}
            okDisabled={loading}
            okText={t("Confirm")}
            closable
            supportText={`${t("Share limit: ")} ${ticket?.share_limit}`}
            >
                {children}
            </Sheet>
        </Portal>);
    }, [open, selectedCustomers, loading, newRecipient?.open]);

    return (
        canShareTicket ? 
        <ShareTicketWrapper>
            <p className="ui-share-ticket-modal-secondary-text">{t("Choose recipients to share your selected ticket with")}</p>
            <br />
            <Select
                showSearch
                onSearch={(query: string) => setFriendSearch(query)}
                onChange={(_, friendObject: ICustomerFriend) => {
                    if (friendObject) {
                        ReactDOM.unstable_batchedUpdates(() => {
                            setSelectedCustomers(prev => [...prev, friendObject]); setFriendSearch("");
                        });
                    }
                }}
                allowClear
                searchValue={friendSearch}
                searching={!friends}
                placeholder={t("Search friends...")}
                usePortal
                showDropDownOnFocus
                label={t("Friends List")}
                noData={
                    <div className="flex items-center p-2 text-black">
                    <FontAwesomeIcon icon={faUsersSlash} fixedWidth size="1x" />
                    <span className="ml-3 italic">No matching customer found...</span>
                    </div>
                }
                >
                    <Option value={"new-value"}>
                        <p onClick={() => {
                        ReactDOM.unstable_batchedUpdates(() => {
                        setNewRecipient(prev => ({...prev, open: true})); setFriendSearch("");
                        });
                        }}>{t("New Recipient")}</p>
                    </Option>
                    
                    {friends?.filter(friend => 
                    !selectedCustomers?.some(selected => selected?.id === friend?.id) 
                    && friend?.full_name?.toLowerCase()?.includes(friendSearch?.toLowerCase()))?.map((friend, index) => {
                        return (
                            <Option key={index} value={friend?.id} extraValues={friend}>
                                {friend?.full_name}
                            </Option>
                        );
                    })}
                </Select>
                {newRecipient?.open ? (
                    <>
                    <Divider />
                    <p className="ui-share-ticket-modal-label">{t("Add New Recipient")}</p>
                    <br />
                    <FormLayout>
                        <FormLayout.Group>
                            <Input 
                            label={t("First Name")} 
                            value={newRecipient?.firstName} 
                            id="firstName" 
                            placeholder={t("First Name")}
                            onChange={e => setNewRecipient(prev => ({...prev, [e.target.id]: e.target.value}))} />
                            <Input 
                            label={t("Last Name")} 
                            value={newRecipient?.lastName} 
                            id="lastName" 
                            placeholder={t("Last Name")}
                            onChange={e => setNewRecipient(prev => ({...prev, [e.target.id]: e.target.value}))} />
                        </FormLayout.Group>
                        <FormLayout.Group>
                            <Input 
                            label={t("Email")} 
                            error={newRecipient?.email !== "" ? !validator.isEmail(newRecipient?.email) : false}
                            value={newRecipient?.email} 
                            id="email" 
                            placeholder={t("Email")}
                            onChange={e => setNewRecipient(prev => ({...prev, [e.target.id]: e.target.value}))} />
                        </FormLayout.Group>
                    </FormLayout>
                    <div className="ml-auto flex">
                        <Button className="ml-auto" size="small" type="secondary" onClick={() => setNewRecipient({open: false, firstName: "", lastName: "", email: ""})}>{t("Cancel")}</Button>
                        <Button size="small" type="primary" onClick={() => 
                            ReactDOM.unstable_batchedUpdates(() => {
                            setSelectedCustomers(prev => [...prev, {
                                id: null, 
                                first_name: newRecipient?.firstName,
                                last_name: newRecipient?.lastName,
                                email: newRecipient?.email,
                                full_name: newRecipient?.firstName + " " + newRecipient?.lastName,
                                initials: "",
                                customer_type: null,
                                phone: "",
                                }]);
                            setNewRecipient({open: false, firstName: "", lastName: "", email: ""});
                        })
                        } disabled={!newRecipient?.firstName || !newRecipient?.lastName || !validator.isEmail(newRecipient?.email)}>{t("Add")}</Button>
                    </div>
                    <Divider />
                    </>
                ) : null}
                {loading ? (
                    <div style={{display: "flex", width: "100%", justifyContent: "center", marginTop: "1rem"}}>
                        <Spin/>
                    </div>
                ) : selectedCustomers ? selectedCustomers?.length > 0 ? (
                    <>
                    <br />
                    <div>
                        <p className="ui-share-ticket-modal-label">{t("Recipient(s)")}</p>
                        {selectedCustomers?.map((customer, customerIndex) => {
                            return (
                                <div key={customerIndex} className="ui-share-ticket-modal-customer-container">
                                    <div className="flex gap-2">
                                        <div className="ui-share-ticket-modal-image-container">
                                            <img src="/public/images/default_profile_image.svg" className="ui-share-ticket-modal-image"/>
                                        </div>
                                        
                                        <div>
                                            <p className="ui-share-ticket-modal-secondary-text"><b>{customer?.full_name}</b></p>
                                            <p className="ui-share-ticket-modal-secondary-text">{customer?.email}</p>
                                        </div>
                                    </div>
                                    <Button size="small" type="link-color" style={{color: "#B42318"}} onClick={() => setSelectedCustomers(prev =>
                                            prev?.filter(filteredCustomer => filteredCustomer?.id !== customer?.id))}>{t("Remove")}</Button>
                                </div>
                            );
                        })}
                    </div>
                    </>
                ) : !ticketShared ? (
                    <div>
                        <br />
                        <Callout type="info" title={t("Ticket Not Shared")} content={t("The selected ticket is not currently shared with anyone. Select a user in the dropdown to share this ticket with them")}/>
                    </div>
                ) : null : null}
        </ShareTicketWrapper> : 
        <Portal isMounted={open}>
            <Popup 
                open={open} 
                title={t("Ticket Cannot Be Shared")} 
                description={t("The selected ticket is not eligible to be shared with other users")} 
                type="info" 
                onOk={onCancel} 
                okText={t("Close")}
                closable
                backDropCancel
                />
        </Portal>
    );
}